import { Add, Delete } from "@mui/icons-material"
import { Divider, Toolbar, Typography, useTheme } from "@mui/material"
import { grey } from "@mui/material/colors"
import { lighten } from "@mui/material/styles"
import React, { ReactNode } from "react"
import { Tool } from "../Tool/Tool"

interface CustomToolbarProps {
    selectedIds?: number[]
    label: string
    addRow?: false | (() => void)
    onDeleteSelectedRows?: () => void
    children?: ReactNode
}

export const NexTableToolbar = ({ selectedIds, label, addRow, onDeleteSelectedRows, children }: CustomToolbarProps) => {
    const numSelected = selectedIds?.length ?? 0

    return (
        <NexTableToolbarBase label={label} selectedRowIds={selectedIds}>
            {children}
            {addRow && <Tool tooltip="Add Row" Icon={Add} onChange={addRow} dataTestId={`${label} add row`} />}
            {onDeleteSelectedRows && (
                <Tool
                    tooltip="Delete"
                    disabled={numSelected === 0}
                    Icon={Delete}
                    value={numSelected !== 0}
                    onChange={onDeleteSelectedRows}
                />
            )}
        </NexTableToolbarBase>
    )
}

export const ToolDivider = () => {
    return <Divider sx={{ mx: 2 }} orientation={"vertical"} variant="middle" flexItem />
}

interface NexTableToolbarBaseProps {
    label: string
    selectedRowIds?: number[]
    children: React.ReactNode
}

export const NexTableToolbarBase = (props: NexTableToolbarBaseProps) => {
    const { selectedRowIds = [], label, children } = props
    const hasRowsSelected = selectedRowIds.length > 0
    const numSelected = selectedRowIds.length

    const theme = useTheme()

    return (
        <Toolbar
            sx={[
                hasRowsSelected && {
                    color: theme.palette.secondary.main,
                    backgroundColor: lighten(theme.palette.secondary.light, 0.85),
                },
                !hasRowsSelected && {
                    color: theme.palette.text.primary,
                    backgroundColor: theme.palette.secondary.dark,
                },
                {
                    pl: 2.5,
                    pr: 2,
                    backgroundColor: grey[50],
                    borderBottom: "1px solid rgba(224,224,224,1)",
                    position: "relative",
                },
            ]}
        >
            {hasRowsSelected && (
                <Typography sx={{ flex: "1 1 100%" }} variant={"subtitle1"}>
                    {numSelected} selected
                </Typography>
            )}
            {!hasRowsSelected && (
                <Typography sx={{ flex: "1 1 100%" }} variant="h6">
                    {label}
                </Typography>
            )}
            {children}
        </Toolbar>
    )
}
